import { ref, uploadBytes, getDownloadURL, deleteObject, getStorage } from 'firebase/storage';
import { auth, firebaseApp, storage } from '@/lib/firebase';
import type { ItineraryAttachment } from './itineraryAttachments';
import {
  DOCUMENT_ALLOWED_EXTENSIONS,
  DOCUMENT_ALLOWED_MIME_TYPES,
  MAX_DOCUMENT_SIZE_BYTES,
  hasAllowedMimeOrExtension,
} from './uploadPolicy';

const id = () => Math.random().toString(36).slice(2, 10);

export type UploadResult = {
  success: boolean;
  url?: string;
  error?: string;
  attachment?: ItineraryAttachment;
};

export type UploadItineraryDocumentOptions = {
  fileName?: string;
  contentType?: string;
};

/**
 * Upload a document for an itinerary item
 * @param tripId - The trip ID
 * @param itemId - The itinerary item ID
 * @param file - The file to upload
 * @returns The download URL of the uploaded file
 */
export const uploadItineraryDocument = async (
  tripId: string,
  itemId: string,
  file: File | Blob,
  options: UploadItineraryDocumentOptions = {}
): Promise<UploadResult> => {
  try {
    const fileCtor = typeof File !== 'undefined' ? File : null;
    const isWebFile = Boolean(fileCtor && file instanceof fileCtor);

    // Validate file size (10MB max)
    if (file.size > MAX_DOCUMENT_SIZE_BYTES) {
      return {
        success: false,
        error: 'File size exceeds 10MB limit',
      };
    }

    const fileType =
      (isWebFile && file.type) ||
      options.contentType ||
      // Some RN Blob implementations include a `type` field.
      ((file as any)?.type as string | undefined) ||
      'application/octet-stream';
    
    const inferredFileName =
      (isWebFile && 'name' in file && typeof file.name === 'string' && file.name) ||
      options.fileName ||
      `document_${Date.now()}`;

    if (!hasAllowedMimeOrExtension(fileType, inferredFileName, DOCUMENT_ALLOWED_MIME_TYPES, DOCUMENT_ALLOWED_EXTENSIONS)) {
      return {
        success: false,
        error: 'File type not supported. Allowed types: PDF, JPG, PNG, DOC, DOCX',
      };
    }

    // Generate unique filename with timestamp
    const timestamp = Date.now();
    const fileName =
      inferredFileName;
    const sanitizedFileName = fileName.replace(/[^a-zA-Z0-9.-]/g, '_');
    const storagePath = `trip-assets/${tripId}/itinerary/${itemId}/documents/${timestamp}_${sanitizedFileName}`;
    const currentUid = auth.currentUser?.uid;
    
    // Create storage reference
    const storageRef = ref(storage, storagePath);
    
    // Upload file
    const snapshot = await uploadBytes(storageRef, file, {
      contentType: fileType,
      customMetadata: currentUid ? { uploadedBy: currentUid } : undefined,
    });
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    const attachment: ItineraryAttachment = {
      id: id(),
      name: sanitizedFileName,
      url: downloadURL,
      contentType: fileType,
      size: file.size,
      source: 'upload',
      createdAt: new Date(),
    };

    return {
      success: true,
      url: downloadURL,
      attachment,
    };
  } catch (error) {
    console.error('Error uploading document:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to upload document',
    };
  }
};

/**
 * Delete a document from storage
 * @param documentUrl - The full URL of the document to delete
 */
export const deleteItineraryDocument = async (documentUrl: string): Promise<boolean> => {
  try {
    const raw = (documentUrl || '').trim();
    if (!raw) return false;

    const defaultBucket = ((storage.app.options.storageBucket as string) || '').replace(/^gs:\/\//i, '');

    const deleteAtPath = async (bucket: string, objectPath: string) => {
      const storageForRef =
        bucket && defaultBucket && bucket !== defaultBucket
          ? getStorage(firebaseApp, `gs://${bucket}`)
          : storage;
      await deleteObject(ref(storageForRef, objectPath));
    };

    if (/^gs:\/\//i.test(raw)) {
      const stripped = raw.replace(/^gs:\/\//i, '');
      const slash = stripped.indexOf('/');
      if (slash < 0) return false;
      const bucket = stripped.slice(0, slash);
      const objectPath = stripped.slice(slash + 1);
      if (!bucket || !objectPath) return false;
      await deleteAtPath(bucket, objectPath);
      return true;
    }

    if (/^https?:\/\//i.test(raw)) {
      try {
        const u = new URL(raw);
        if (u.hostname === 'storage.googleapis.com') {
          const segments = u.pathname.replace(/^\/+/, '').split('/').filter(Boolean);
          if (segments.length >= 2) {
            const bucket = decodeURIComponent(segments[0]);
            const objectPath = segments
              .slice(1)
              .map((seg) => decodeURIComponent(seg))
              .join('/');
            await deleteAtPath(bucket, objectPath);
            return true;
          }
        }
      } catch {
        /* fall through */
      }

      // Firebase download URLs: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{path}?alt=media
      const urlParts = raw.split('/o/');
      if (urlParts.length < 2) {
        console.error('Invalid document URL format');
        return false;
      }

      const pathWithQuery = urlParts[1];
      const path = decodeURIComponent(pathWithQuery.split('?')[0]);
      await deleteObject(ref(storage, path));
      return true;
    }

    console.error('Invalid document URL format');
    return false;
  } catch (error) {
    console.error('Error deleting document:', error);
    return false;
  }
};

/**
 * Upload multiple documents for an itinerary item
 * @param tripId - The trip ID
 * @param itemId - The itinerary item ID
 * @param files - Array of files to upload
 * @returns Array of upload results with URLs
 */
export const uploadMultipleItineraryDocuments = async (
  tripId: string,
  itemId: string,
  files: (File | Blob)[]
): Promise<UploadResult[]> => {
  const results: UploadResult[] = [];
  
  for (const file of files) {
    const result = await uploadItineraryDocument(tripId, itemId, file);
    results.push(result);
  }
  
  return results;
};

